import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutRectangle } from 'react-native';
import { Caption, Headline, HoverCard } from 'src/components';
import PatientAvatar from 'src/components/PatientAvatar';
import { IconSize, Margin, MOBILE_WIDTH_MAX, PATIENT_STATUSES } from 'src/constants';
import { usePatient } from 'src/hooks';
import { Patient } from 'src/interfaces';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import MainStackParamsList from 'src/routes/stacks/MainStack/ParamsList';
import { Screens } from 'src/routes/stacks/screens';
import { palette } from 'src/theme';
import { layout } from 'src/theme/globalStyles';
import styled from 'styled-components/native';

interface Props {
  numCols: number;
  patientId: Patient['patientId'];
  isFromRequestWizard?: boolean;
}

const PatientCard: React.FC<Props> = ({ patientId, numCols, isFromRequestWizard }) => {
  const navigation =
    useNavigation<
      NativeStackNavigationProp<MainStackParamsList, Screens.PATIENT_PROFILE, undefined>
    >();
  const { colors } = useAppTheme();
  const [l, setLayout] = useState<LayoutRectangle>();
  const { t } = useTranslation('petList');
  const { data: patient } = usePatient(patientId, { suspense: false });

  const goToPatientProfile = (patientId: string) => {
    navigation.navigate(Screens.PATIENT_PROFILE, { id: patientId, isFromRequestWizard });
  };

  const patientStatus = useMemo(() => {
    if (patient?.deceasedDate) {
      return PATIENT_STATUSES.DECEASED;
    }
    return patient?.status as PATIENT_STATUSES;
  }, [patient]);

  return (
    <Holder numCols={numCols}>
      <Container onPress={() => goToPatientProfile(patientId)}>
        <ImageContainer onLayout={(e) => setLayout(e.nativeEvent.layout)}>
          <StyledPatientAvatar patient={patient} size={l?.width ?? IconSize.XXXL} />
          {patientStatus && patientStatus !== PATIENT_STATUSES.ACTIVE && (
            <StatusContainer>
              <Caption color={palette.AB_BRIGHT_WHITE}>{t(patientStatus)}</Caption>
            </StatusContainer>
          )}
        </ImageContainer>
        <NameContainer>
          <Headline color={colors.onSurface} style={layout.alignSelfCenter}>
            {patient?.name}
          </Headline>
        </NameContainer>
      </Container>
    </Holder>
  );
};

export default PatientCard;

const Holder = styled.View<{ numCols: number }>`
  width: ${({ numCols, theme }) =>
    numCols > 1 ? Math.floor((1 / numCols) * theme.viewMode.maxContentWidth) : MOBILE_WIDTH_MAX}px;
  max-width: 100%;
`;

const Container = styled(HoverCard)`
  border-radius: ${({ theme }) => theme.roundness}px;
  align-items: center;
  margin: ${Margin.Medium}px;
  cursor: pointer;
`;

const ImageContainer = styled.View`
  border-top-right-radius: ${({ theme }) => theme.roundness}px;
  border-top-left-radius: ${({ theme }) => theme.roundness}px;
  width: 100%;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
`;

const StatusContainer = styled.View`
  position: absolute;
  bottom: 0px;
  right: 0px;
  padding: ${Margin.Small}px;
  background-color: ${({ theme }) => theme.colors.backdrop};
  border-top-left-radius: ${({ theme }) => theme.roundness}px;
`;

const NameContainer = styled.View`
  padding: ${Margin.Medium}px;
`;

const StyledPatientAvatar = styled(PatientAvatar)`
  border-top-left-radius: ${({ theme }) => theme.roundness}px;
  border-top-right-radius: ${({ theme }) => theme.roundness}px;
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
`;
